/**
 * Infant Crib Card Tab Component
 * Quick reference card to hang above infant cribs for substitute caregivers
 * With save to database, history view, and delete functionality
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Baby, Printer, Heart, Milk, Moon, Clock, Star, Save, History, ChevronLeft, Trash2, CheckSquare, Square, Download } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import axios from "axios";
import { exportCribCardPDF } from "@/utils/pdfExport";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InfantCribCardTab = ({ children, centerName = "Childcare Center", staff = [] }) => {
  const [view, setView] = useState("form");
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedChild, setSelectedChild] = useState("");
  const [staffName, setStaffName] = useState("");
  const [selectedStaff, setSelectedStaff] = useState("");
  const [showManualStaffInput, setShowManualStaffInput] = useState(false);
  
  // Basic Info
  const [childName, setChildName] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [parentNames, setParentNames] = useState("");
  const [emergencyPhone, setEmergencyPhone] = useState("");
  
  // Formula/Feeding Info
  const [formulaType, setFormulaType] = useState("");
  const [formulaOunces, setFormulaOunces] = useState("");
  const [feedingSchedule, setFeedingSchedule] = useState([
    { id: 1, time: "", notes: "" },
    { id: 2, time: "", notes: "" },
    { id: 3, time: "", notes: "" },
    { id: 4, time: "", notes: "" }
  ]);
  
  // Sleep Pattern
  const [napSchedule, setNapSchedule] = useState([
    { id: 1, time: "Morning", duration: "" },
    { id: 2, time: "Afternoon", duration: "" }
  ]);
  const [sleepPosition, setSleepPosition] = useState("back");
  const [sleepNotes, setSleepNotes] = useState("");
  
  // Preferences & Special Info
  const [likes, setLikes] = useState("");
  const [dislikes, setDislikes] = useState("");
  const [soothingTechniques, setSoothingTechniques] = useState("");
  const [allergies, setAllergies] = useState("");
  const [specialInstructions, setSpecialInstructions] = useState("");

  // Filter to show only Full Time and Part Time children
  const displayChildren = children.filter(c => {
    if (c.active === false) return false;
    const category = c.category?.toLowerCase().replace(/[\s_-]/g, '') || '';
    return category === 'fulltime' || category === 'parttime' || 
           category === 'full_time' || category === 'part_time' ||
           category === 'full-time' || category === 'part-time';
  });

  const handleChildSelect = (childId) => {
    setSelectedChild(childId);
    if (childId) {
      const child = children.find(c => c.id === childId);
      if (child) {
        setChildName(child.name || "");
        setBirthdate(child.birthdate || child.dateOfBirth || "");
      }
    } else {
      // Clear the name and birthdate when reverting to "Select infant..."
      setChildName("");
      setBirthdate("");
    }
  };

  const handleStaffSelect = (value) => {
    if (value === "manual") {
      setShowManualStaffInput(true);
      setSelectedStaff("");
      setStaffName("");
    } else {
      setShowManualStaffInput(false);
      setSelectedStaff(value);
      const staffMember = staff.find(s => s.id === value || s.name === value);
      setStaffName(staffMember?.name || value);
    }
  };

  // Fetch saved crib cards
  const fetchSavedCards = async () => {
    setLoadingCards(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/crib-cards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedCards(response.data || []);
    } catch (error) {
      console.error("Error fetching crib cards:", error);
      toast.error("Failed to load saved crib cards");
    } finally {
      setLoadingCards(false);
    }
  };

  useEffect(() => {
    if (view === "history") {
      fetchSavedCards();
    }
  }, [view]);

  // Save crib card to database
  const handleSave = async () => {
    if (!childName) {
      toast.error("Please enter a child name");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const cardData = {
        childId: selectedChild,
        childName,
        staffName,
        birthdate,
        parentNames,
        emergencyPhone,
        formulaType,
        formulaOunces,
        feedingSchedule,
        napSchedule,
        sleepPosition,
        sleepNotes,
        likes,
        dislikes,
        soothingTechniques,
        allergies,
        specialInstructions
      };

      await axios.post(`${API}/crib-cards`, cardData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Crib card saved successfully!");
      clearForm();
    } catch (error) {
      console.error("Error saving crib card:", error);
      toast.error("Failed to save crib card");
    } finally {
      setSaving(false);
    }
  };

  // Delete single crib card
  const handleDeleteCard = async (cardId) => {
    if (!window.confirm("Are you sure you want to delete this crib card?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/crib-cards/${cardId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Crib card deleted");
      fetchSavedCards();
    } catch (error) {
      console.error("Error deleting crib card:", error);
      toast.error("Failed to delete crib card");
    }
  };

  // Bulk delete crib cards
  const handleBulkDelete = async () => {
    if (selectedCards.length === 0) {
      toast.error("No crib cards selected");
      return;
    }
    
    if (!window.confirm(`Delete ${selectedCards.length} selected crib cards?`)) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/crib-cards/bulk-delete`, 
        { ids: selectedCards },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Deleted ${selectedCards.length} crib cards`);
      setSelectedCards([]);
      fetchSavedCards();
    } catch (error) {
      console.error("Error bulk deleting:", error);
      toast.error("Failed to delete crib cards");
    }
  };

  // Toggle card selection
  const toggleCardSelection = (cardId) => {
    setSelectedCards(prev => 
      prev.includes(cardId) 
        ? prev.filter(id => id !== cardId)
        : [...prev, cardId]
    );
  };

  // Select/Deselect all
  const toggleSelectAll = () => {
    if (selectedCards.length === savedCards.length) {
      setSelectedCards([]);
    } else {
      setSelectedCards(savedCards.map(c => c.id));
    }
  };

  // Load crib card into form
  const loadCard = (card) => {
    setSelectedChild(card.childId || "");
    setChildName(card.childName || "");
    setStaffName(card.staffName || "");
    setSelectedStaff("");
    setShowManualStaffInput(!!card.staffName);
    setBirthdate(card.birthdate || "");
    setParentNames(card.parentNames || "");
    setEmergencyPhone(card.emergencyPhone || "");
    setFormulaType(card.formulaType || "");
    setFormulaOunces(card.formulaOunces || "");
    setFeedingSchedule(card.feedingSchedule?.length > 0 ? card.feedingSchedule : [
      { id: 1, time: "", notes: "" },
      { id: 2, time: "", notes: "" },
      { id: 3, time: "", notes: "" },
      { id: 4, time: "", notes: "" }
    ]);
    setNapSchedule(card.napSchedule?.length > 0 ? card.napSchedule : [
      { id: 1, time: "Morning", duration: "" },
      { id: 2, time: "Afternoon", duration: "" }
    ]);
    setSleepPosition(card.sleepPosition || "back");
    setSleepNotes(card.sleepNotes || "");
    setLikes(card.likes || "");
    setDislikes(card.dislikes || "");
    setSoothingTechniques(card.soothingTechniques || "");
    setAllergies(card.allergies || "");
    setSpecialInstructions(card.specialInstructions || "");
    setView("form");
  };

  const updateFeedingSchedule = (id, field, value) => {
    setFeedingSchedule(feedingSchedule.map(f => f.id === id ? { ...f, [field]: value } : f));
  };

  const updateNapSchedule = (id, field, value) => {
    setNapSchedule(napSchedule.map(n => n.id === id ? { ...n, [field]: value } : n));
  };

  const handlePrint = () => {
    window.print();
  };

  const clearForm = () => {
    setSelectedChild("");
    setChildName("");
    setStaffName("");
    setSelectedStaff("");
    setShowManualStaffInput(false);
    setBirthdate("");
    setParentNames("");
    setEmergencyPhone("");
    setFormulaType("");
    setFormulaOunces("");
    setFeedingSchedule([
      { id: 1, time: "", notes: "" },
      { id: 2, time: "", notes: "" },
      { id: 3, time: "", notes: "" },
      { id: 4, time: "", notes: "" }
    ]);
    setNapSchedule([
      { id: 1, time: "Morning", duration: "" },
      { id: 2, time: "Afternoon", duration: "" }
    ]);
    setSleepPosition("back");
    setSleepNotes("");
    setLikes("");
    setDislikes("");
    setSoothingTechniques("");
    setAllergies("");
    setSpecialInstructions("");
  };

  const calculateAge = (dob) => {
    if (!dob) return "";
    const birth = new Date(dob);
    const today = new Date();
    const months = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
    if (months < 1) {
      const days = Math.floor((today - birth) / (1000 * 60 * 60 * 24));
      return `${days} days`;
    }
    return `${months} months`;
  };

  // History View
  if (view === "history") {
    return (
      <Card className="bg-white/90 backdrop-blur-sm border-2 border-blue-200" data-testid="infant-crib-card-history">
        <CardHeader className="bg-gradient-to-r from-blue-100 to-cyan-100">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl text-blue-800 flex items-center gap-3">
              <History className="w-7 h-7" />
              Saved Crib Cards
            </CardTitle>
            <Button 
              onClick={() => setView("form")} 
              variant="outline" 
              className="border-blue-300 text-blue-600"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to Form
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Bulk Actions */}
          <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Button
                onClick={toggleSelectAll}
                variant="outline"
                size="sm"
                className="border-blue-300"
                data-testid="select-all-crib-cards"
              >
                {selectedCards.length === savedCards.length && savedCards.length > 0 ? (
                  <><CheckSquare className="w-4 h-4 mr-1" /> Deselect All</>
                ) : (
                  <><Square className="w-4 h-4 mr-1" /> Select All</>
                )}
              </Button>
              <span className="text-sm text-gray-600">
                {selectedCards.length} of {savedCards.length} selected
              </span>
            </div>
            {selectedCards.length > 0 && (
              <Button
                onClick={handleBulkDelete}
                variant="destructive"
                size="sm"
                data-testid="bulk-delete-crib-cards"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete Selected ({selectedCards.length})
              </Button>
            )}
          </div>

          {/* Cards List */}
          {loadingCards ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading crib cards...</p>
            </div>
          ) : savedCards.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Baby className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No saved crib cards yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {savedCards.map((card) => (
                <div 
                  key={card.id} 
                  className={`p-4 rounded-lg border-2 transition-colors ${
                    selectedCards.includes(card.id) 
                      ? 'border-blue-400 bg-blue-50' 
                      : 'border-gray-200 hover:border-blue-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={selectedCards.includes(card.id)}
                        onCheckedChange={() => toggleCardSelection(card.id)}
                        className="border-blue-400 data-[state=checked]:bg-blue-500 mt-1"
                      />
                      <div>
                        <p className="font-bold text-blue-800 text-lg">{card.childName}</p>
                        {card.birthdate && (
                          <p className="text-sm text-gray-500">
                            DOB: {format(new Date(card.birthdate + 'T12:00:00'), 'MMM d, yyyy')} • Age: {calculateAge(card.birthdate)}
                          </p>
                        )}
                        <div className="mt-2 space-y-1 text-xs text-gray-600">
                          {card.parentNames && <p>Parents: {card.parentNames}</p>}
                          {card.formulaType && <p>Formula: {card.formulaType} ({card.formulaOunces} oz)</p>}
                          {card.allergies && <p className="text-red-600 font-medium">⚠️ Allergies: {card.allergies.substring(0, 30)}...</p>}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => loadCard(card)}
                        variant="outline"
                        size="sm"
                        className="border-blue-300 text-blue-600"
                      >
                        View/Edit
                      </Button>
                      <Button
                        onClick={() => handleDeleteCard(card.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Form View
  return (
    <Card className="bg-white/90 backdrop-blur-sm border-2 border-blue-200" data-testid="infant-crib-card-tab">
      <CardHeader className="print:hidden bg-gradient-to-r from-blue-100 to-cyan-100">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl text-blue-800 flex items-center gap-3">
              <Baby className="w-7 h-7" />
              Infant Crib Card
            </CardTitle>
            <p className="text-blue-600 text-sm mt-1">Quick reference card for substitute caregivers - hang above crib</p>
          </div>
          <Button 
            onClick={() => setView("history")} 
            variant="outline" 
            className="border-blue-300 text-blue-600"
            data-testid="view-crib-card-history"
          >
            <History className="w-4 h-4 mr-1" /> View Saved ({savedCards.length})
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {/* Form Controls - Hidden when printing */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 print:hidden">
          <div>
            <Label className="text-blue-800 font-semibold">Select Child</Label>
            <select
              value={selectedChild}
              onChange={(e) => handleChildSelect(e.target.value)}
              className="w-full p-2 border border-blue-200 rounded mt-1"
              data-testid="crib-card-child-select"
            >
              <option value="">Select infant...</option>
              {displayChildren.map(child => (
                <option key={child.id} value={child.id}>{child.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-blue-800 font-semibold">Staff Name</Label>
            <select
              value={showManualStaffInput ? "manual" : selectedStaff}
              onChange={(e) => handleStaffSelect(e.target.value)}
              className="w-full p-2 border border-blue-200 rounded mt-1"
              data-testid="crib-card-staff-select"
            >
              <option value="">Select staff...</option>
              {staff.map(s => (
                <option key={s.id || s.name} value={s.id || s.name}>{s.name}</option>
              ))}
              <option value="manual">-- Enter manually --</option>
            </select>
            {showManualStaffInput && (
              <Input
                value={staffName}
                onChange={(e) => setStaffName(e.target.value)}
                placeholder="Enter substitute name..."
                className="border-blue-200 mt-2"
                data-testid="crib-card-staff-manual"
              />
            )}
          </div>
          <div className="flex items-end gap-2 md:col-span-2 flex-wrap">
            <Button onClick={clearForm} variant="outline" className="border-blue-300 text-blue-600">
              Clear Form
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="save-crib-card"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Card"}
            </Button>
            <Button 
              onClick={() => exportCribCardPDF({
                childId: selectedChild,
                childName: childName,
                staffName: staffName,
                birthdate: birthdate,
                parentNames: parentNames,
                emergencyPhone: emergencyPhone,
                formulaType: formulaType,
                formulaOunces: formulaOunces,
                feedingSchedule: feedingSchedule,
                napSchedule: napSchedule,
                sleepPosition: sleepPosition,
                sleepNotes: sleepNotes,
                likes: likes,
                dislikes: dislikes,
                soothingTechniques: soothingTechniques,
                allergies: allergies,
                specialInstructions: specialInstructions
              }, centerName)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="export-crib-pdf"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Printer className="w-4 h-4 mr-2" />
              Print Card
            </Button>
          </div>
        </div>

        {/* Printable Crib Card - Compact for single page */}
        <div className="border-4 border-blue-300 rounded-xl p-4 bg-gradient-to-br from-blue-50 to-white print:border-2 print:border-gray-600 print:bg-white print:p-3">
          {/* Header with Baby Info */}
          <div className="text-center mb-3 pb-2 border-b-2 border-blue-200 print:border-gray-400 print:mb-2 print:pb-1">
            <div className="flex justify-center mb-1">
              <div className="bg-blue-500 text-white px-4 py-1 rounded-full text-lg font-bold print:bg-gray-700 print:text-sm">
                <Baby className="inline w-5 h-5 mr-1" />
                INFANT CARE CARD
              </div>
            </div>
            
            {/* Staff Name on card */}
            <div className="mt-1 text-sm">
              <span className="font-semibold">Staff Name:</span>{" "}
              <span className="text-blue-700 print:text-black">{staffName || "________________"}</span>
            </div>
            
            {/* Name and Basic Info */}
            <div className="mt-2 space-y-1">
              <div className="flex items-center justify-center gap-2">
                <Label className="font-bold text-sm">Name:</Label>
                <Input
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                  className="max-w-[180px] text-lg font-bold text-center border-blue-300 h-8 print:border-b-2 print:border-gray-600 print:rounded-none print:h-6 print:text-base"
                  placeholder="Baby's Name"
                  data-testid="crib-card-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-2 max-w-sm mx-auto">
                <div className="flex items-center gap-1">
                  <Label className="font-semibold text-xs">DOB:</Label>
                  <Input
                    type="date"
                    value={birthdate}
                    onChange={(e) => setBirthdate(e.target.value)}
                    className="text-xs border-blue-200 h-7 print:border-b print:border-gray-400 print:rounded-none print:h-5"
                    data-testid="crib-card-dob"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Label className="font-semibold text-xs">Age:</Label>
                  <span className="text-xs font-medium text-blue-700 print:text-black">
                    {calculateAge(birthdate) || "___________"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Parent/Emergency Contact - More Compact */}
          <div className="mb-2 p-2 bg-red-50 rounded-lg border border-red-200 print:bg-white print:border-red-400 print:p-1">
            <h3 className="font-bold text-red-700 text-sm mb-1 flex items-center gap-1">
              <span>🚨</span> Emergency Contact
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-medium">Parent(s):</Label>
                <Input
                  value={parentNames}
                  onChange={(e) => setParentNames(e.target.value)}
                  placeholder="Mom & Dad's names"
                  className="text-xs border-red-200 h-7 print:h-5"
                  data-testid="crib-card-parents"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">Phone:</Label>
                <Input
                  value={emergencyPhone}
                  onChange={(e) => setEmergencyPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="text-xs border-red-200 font-bold h-7 print:h-5"
                  data-testid="crib-card-phone"
                />
              </div>
            </div>
          </div>

          {/* Main Content Grid - More Compact */}
          <div className="grid grid-cols-2 gap-2 mb-2">
            {/* Formula/Feeding Section */}
            <div className="border-2 border-cyan-200 rounded-lg p-2 bg-cyan-50 print:bg-white print:border-gray-400 print:p-1">
              <h3 className="font-bold text-cyan-800 print:text-black flex items-center gap-1 mb-1 text-sm">
                <Milk className="w-4 h-4" />
                Formula / Feeding
              </h3>
              <div className="space-y-1">
                <div className="grid grid-cols-2 gap-1">
                  <div>
                    <Label className="text-xs font-medium">Type:</Label>
                    <Input
                      value={formulaType}
                      onChange={(e) => setFormulaType(e.target.value)}
                      placeholder="Similac..."
                      className="text-xs border-cyan-200 h-6"
                      data-testid="crib-card-formula-type"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Oz:</Label>
                    <Input
                      value={formulaOunces}
                      onChange={(e) => setFormulaOunces(e.target.value)}
                      placeholder="4-6"
                      className="text-xs border-cyan-200 font-bold h-6"
                      data-testid="crib-card-formula-oz"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-xs font-medium">Feeding Times:</Label>
                  <div className="grid grid-cols-2 gap-1">
                    {feedingSchedule.map((feed) => (
                      <Input
                        key={feed.id}
                        type="time"
                        value={feed.time}
                        onChange={(e) => updateFeedingSchedule(feed.id, 'time', e.target.value)}
                        className="w-full h-6 text-xs border-cyan-200"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Sleep Pattern Section */}
            <div className="border-2 border-indigo-200 rounded-lg p-2 bg-indigo-50 print:bg-white print:border-gray-400 print:p-1">
              <h3 className="font-bold text-indigo-800 print:text-black flex items-center gap-1 mb-1 text-sm">
                <Moon className="w-4 h-4" />
                Sleep Pattern
              </h3>
              <div className="space-y-1">
                <div>
                  <Label className="text-xs font-medium">Naps:</Label>
                  {napSchedule.map((nap) => (
                    <div key={nap.id} className="flex items-center gap-1 mb-1">
                      <span className="text-xs w-16">{nap.time}:</span>
                      <Input
                        value={nap.duration}
                        onChange={(e) => updateNapSchedule(nap.id, 'duration', e.target.value)}
                        placeholder="1-2 hrs"
                        className="flex-1 h-6 text-xs border-indigo-200"
                      />
                    </div>
                  ))}
                </div>
                
                <div>
                  <Label className="text-xs font-medium">Position:</Label>
                  <div className="flex gap-2">
                    {['back', 'side', 'stomach'].map((pos) => (
                      <label key={pos} className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name="sleepPosition"
                          checked={sleepPosition === pos}
                          onChange={() => setSleepPosition(pos)}
                          className="w-3 h-3"
                        />
                        <span className="text-xs capitalize">{pos}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <Input
                  value={sleepNotes}
                  onChange={(e) => setSleepNotes(e.target.value)}
                  placeholder="Sleep notes..."
                  className="text-xs border-indigo-200 h-6"
                  data-testid="crib-card-sleep-notes"
                />
              </div>
            </div>
          </div>

          {/* Likes/Dislikes - Single Row */}
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="border-2 border-green-200 rounded-lg p-2 bg-green-50 print:bg-white print:border-gray-400 print:p-1">
              <h3 className="font-bold text-green-800 print:text-black flex items-center gap-1 mb-1 text-sm">
                <Heart className="w-4 h-4" />
                Things I Like
              </h3>
              <Textarea
                value={likes}
                onChange={(e) => setLikes(e.target.value)}
                placeholder="Being held, music..."
                className="text-xs border-green-200 min-h-[40px] print:min-h-[30px]"
                data-testid="crib-card-likes"
              />
            </div>
            
            <div className="border-2 border-orange-200 rounded-lg p-2 bg-orange-50 print:bg-white print:border-gray-400 print:p-1">
              <h3 className="font-bold text-orange-800 print:text-black mb-1 text-sm">
                ⚠️ Things I Don't Like
              </h3>
              <Textarea
                value={dislikes}
                onChange={(e) => setDislikes(e.target.value)}
                placeholder="Loud noises..."
                className="text-xs border-orange-200 min-h-[40px] print:min-h-[30px]"
                data-testid="crib-card-dislikes"
              />
            </div>
          </div>

          {/* Soothing Techniques - Compact */}
          <div className="border-2 border-pink-200 rounded-lg p-2 bg-pink-50 mb-2 print:bg-white print:border-gray-400 print:p-1">
            <h3 className="font-bold text-pink-800 print:text-black flex items-center gap-1 mb-1 text-sm">
              <Star className="w-4 h-4" />
              How to Soothe Me
            </h3>
            <Textarea
              value={soothingTechniques}
              onChange={(e) => setSoothingTechniques(e.target.value)}
              placeholder="Gentle bouncing, singing..."
              className="text-xs border-pink-200 min-h-[35px] print:min-h-[25px]"
              data-testid="crib-card-soothing"
            />
          </div>

          {/* Allergies & Special Instructions - Single Row */}
          <div className="grid grid-cols-2 gap-2">
            <div className="border-2 border-red-300 rounded-lg p-2 bg-red-50 print:bg-white print:border-red-500 print:p-1">
              <h3 className="font-bold text-red-700 print:text-red-800 mb-1 text-sm">
                🚫 Allergies / Medical
              </h3>
              <Textarea
                value={allergies}
                onChange={(e) => setAllergies(e.target.value)}
                placeholder="No known allergies..."
                className="text-xs border-red-200 min-h-[35px] font-medium print:min-h-[25px]"
                data-testid="crib-card-allergies"
              />
            </div>
            
            <div className="border-2 border-yellow-300 rounded-lg p-2 bg-yellow-50 print:bg-white print:border-yellow-500 print:p-1">
              <h3 className="font-bold text-yellow-700 print:text-yellow-800 mb-1 text-sm">
                📝 Special Instructions
              </h3>
              <Textarea
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                placeholder="Other important info..."
                className="text-xs border-yellow-200 min-h-[35px] print:min-h-[25px]"
                data-testid="crib-card-special"
              />
            </div>
          </div>

          {/* Footer - Compact */}
          <div className="text-center mt-3 pt-2 border-t-2 border-blue-200 print:border-gray-400 print:mt-2 print:pt-1">
            <p className="text-blue-600 font-semibold text-sm print:text-gray-600">{centerName}</p>
            <p className="text-xs text-gray-500">Updated: {format(new Date(), 'MM/dd/yyyy')}</p>
          </div>
        </div>

        {/* Print Instructions */}
        <div className="mt-4 p-3 bg-blue-50 rounded-lg print:hidden">
          <p className="text-sm text-blue-700">
            <strong>Tip:</strong> Print this card and laminate it for durability. Hang it above the infant's crib for easy reference by substitute caregivers.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default InfantCribCardTab;
